import React, { useEffect, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import api from '../services/api';
import { F1_DRIVERS, driverImgUrl } from '../constants/f1Drivers';

const SEASON = 2026;
const F1_POINTS = { 1:25, 2:18, 3:15, 4:12, 5:10, 6:8, 7:6, 8:4, 9:2, 10:1 };

const posColor = (p) => p === 1 ? 'text-yellow-400' : p <= 3 ? 'text-orange-400' : 'text-zinc-400';

const StatBox = ({ label, value, color }) => (
  <div className="rounded-xl p-4 text-center" style={{ background: (color || '#e10600') + '12' }}>
    <p className="text-2xl font-black text-white">{value ?? '—'}</p>
    <p className="text-[10px] uppercase tracking-wider text-zinc-500 mt-0.5">{label}</p>
  </div>
);

const DriverPage = () => {
  const { number } = useParams();
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);

  const driver = F1_DRIVERS.find(d => String(d.number) === String(number));

  useEffect(() => {
    api.get(`/races/driver-stats/${number}?season=${SEASON}`)
      .then(r => setStats(r.data))
      .catch(() => setStats(null))
      .finally(() => setLoading(false));
  }, [number]);

  if (!driver) return (
    <div className="max-w-3xl mx-auto px-4 py-12 text-center text-zinc-400">Pilote introuvable.</div>
  );

  return (
    <div className="max-w-3xl mx-auto px-4 py-10">
      <Link to="/standings" className="text-zinc-500 hover:text-zinc-300 text-sm flex items-center gap-1 mb-6">
        ← Classement
      </Link>

      {/* Hero */}
      <div className="card mb-6 overflow-hidden relative"
        style={{ borderColor: driver.color + '55' }}>
        <div className="absolute inset-0 pointer-events-none"
          style={{ background: `linear-gradient(135deg, ${driver.color}15 0%, transparent 60%)` }} />
        <div className="flex items-end gap-6 relative z-10">
          <div className="w-32 h-32 shrink-0 rounded-xl overflow-hidden"
            style={{ background: driver.color + '20' }}>
            <img src={driverImgUrl(driver.img)} alt={driver.name}
              className="w-full h-full object-cover object-top"
              onError={e => { e.target.style.display = 'none'; }} />
          </div>
          <div className="pb-2">
            <p className="text-[11px] font-bold uppercase tracking-widest mb-1" style={{ color: driver.color }}>
              {driver.team}
            </p>
            <h1 className="text-3xl font-black text-white leading-tight">{driver.name}</h1>
            <p className="text-zinc-500 text-sm mt-1">#{driver.number} · Saison {SEASON}</p>
          </div>
        </div>
      </div>

      {loading ? (
        <div className="text-center text-zinc-400 py-12">Chargement des stats...</div>
      ) : !stats || stats.racesRun === 0 ? (
        <div className="card text-center py-10 text-zinc-500">
          Aucun résultat disponible pour la saison {SEASON}.
        </div>
      ) : (
        <>
          {/* Stats grid */}
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-6">
            <StatBox label="Classement" value={stats.champPosition != null ? `P${stats.champPosition}` : '—'} color={driver.color} />
            <StatBox label="Points" value={stats.totalPoints} color={driver.color} />
            <StatBox label="Victoires" value={stats.wins} color={driver.color} />
            <StatBox label="Podiums" value={stats.podiums} color={driver.color} />
          </div>

          {/* Per-race results */}
          <div className="card">
            <h2 className="font-bold text-white mb-4">Résultats {SEASON}</h2>
            <div className="flex flex-col">
              {stats.results.map((r, i) => (
                <div key={r.round}
                  className="flex items-center justify-between py-2.5 border-b border-zinc-800 last:border-0">
                  <div className="flex items-center gap-3">
                    <span className="text-zinc-600 text-xs w-6 text-right">{r.round}</span>
                    <span className="text-zinc-300 text-sm">{r.raceName}</span>
                  </div>
                  <div className="flex items-center gap-3 shrink-0">
                    {r.points > 0 && (
                      <span className="text-[11px] text-zinc-500">+{r.points} pts</span>
                    )}
                    <span className={`font-black text-sm w-8 text-right ${posColor(r.position)}`}>P{r.position}</span>
                  </div>
                </div>
              ))}
            </div>
            <div className="mt-4 pt-4 border-t border-zinc-800 flex justify-between text-sm">
              <span className="text-zinc-500">{stats.racesRun} courses · Meilleur: {stats.bestResult != null ? `P${stats.bestResult}` : '—'}</span>
              <span className="font-black text-white">{stats.totalPoints} pts</span>
            </div>
          </div>
        </>
      )}
    </div>
  );
};

export default DriverPage;
